import Checkbox from '@/components/_rebrand/Checkbox';
import InputField from '@/components/_rebrand/InputField';
import PhoneField from '@/components/_rebrand/PhoneField';
import SelectField from '@/components/_rebrand/SelectField';
import { Text } from '@/components/_rebrand/Text';
import {
  HubspotCompanySizeDropdownOptions,
  HubspotFieldLabels,
  HubspotFieldNames,
  HubspotIndustryDropdownOptions
} from '@/constants/hubspot';
import { getFormikFieldErrorFunction } from '@/lib/formik';
import { FormikProps } from 'formik';
import { FC } from 'react';
import { ButtonElement } from '../Button';
import { DisclaimerText } from '../DisclaimerText';
import { EmailState, GdprOptInState } from './TrialForm.hooks';

export type TrialFormStepFields = {
  [HubspotFieldNames.CompanyName]: string;
  [HubspotFieldNames.CompanySize]: string;
  [HubspotFieldNames.Email]: string;
  [HubspotFieldNames.FirstName]: string;
  [HubspotFieldNames.Industry]: string;
  [HubspotFieldNames.JobTitle]: string;
  [HubspotFieldNames.LastName]: string;
  [HubspotFieldNames.Phone]: string;
  [HubspotFieldNames.MarketingEmailOptOut]: boolean;
  password?: string;
}

type TrialPersonalInfoFormProps = {
  formik: FormikProps<TrialFormStepFields>;
  gdprOptIn: GdprOptInState | null;
  emailState: EmailState | null;
}

const TrialPersonalInfoForm: FC<TrialPersonalInfoFormProps> = ({ formik, gdprOptIn, emailState }) => {
  const getFieldErrorMessage = getFormikFieldErrorFunction(formik);
  const emailError = getFieldErrorMessage(HubspotFieldNames.Email);
  const firstNameError = getFieldErrorMessage(HubspotFieldNames.FirstName);
  const lastNameError = getFieldErrorMessage(HubspotFieldNames.LastName);
  const companyNameError = getFieldErrorMessage(HubspotFieldNames.CompanyName);
  const jobTitleError = getFieldErrorMessage(HubspotFieldNames.JobTitle);
  const industryError = getFieldErrorMessage(HubspotFieldNames.Industry);
  const phoneError = getFieldErrorMessage(HubspotFieldNames.Phone);
  const companySizeError = getFieldErrorMessage(HubspotFieldNames.CompanySize);

  const isSubmitDisabled = formik.isValidating || formik.isSubmitting || emailState?.checking || !!emailError || !!firstNameError || !!lastNameError || !!companyNameError || !!jobTitleError || !!industryError || !!phoneError || !!companySizeError;

  return (
    <form id="trial-form-step-one" className="flex flex-col gap-y-s" onSubmit={formik.handleSubmit} noValidate>
      <InputField
        type="email"
        name={HubspotFieldNames.Email}
        label={HubspotFieldLabels.Email}
        placeholder="you@company.com"
        value={formik.values[HubspotFieldNames.Email]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        errorMessage={emailError}
        warningMessage={emailState?.isNotWorkEmail ? "It's best to sign up with your work email" : undefined}
        loading={emailState?.checking}
      />
      <fieldset className="grid grid-cols-2 gap-s">
        <InputField
          type="text"
          name={HubspotFieldNames.FirstName}
          label={HubspotFieldLabels.FirstName}
          placeholder="Jamie"
          value={formik.values[HubspotFieldNames.FirstName]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorMessage={firstNameError}
        />

        <InputField
          type="text"
          name={HubspotFieldNames.LastName}
          label={HubspotFieldLabels.LastName}
          placeholder="Smith"
          value={formik.values[HubspotFieldNames.LastName]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorMessage={lastNameError}
        />
      </fieldset>

      <fieldset className="grid grid-cols-2 gap-s">
        <InputField
          type="text"
          name={HubspotFieldNames.CompanyName}
          label={HubspotFieldLabels.CompanyName}
          placeholder="Your Company"
          value={formik.values[HubspotFieldNames.CompanyName]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorMessage={companyNameError}
        />

        <InputField
          type="text"
          name={HubspotFieldNames.JobTitle}
          label={HubspotFieldLabels.JobTitle}
          placeholder="e.g., Director of Operations"
          value={formik.values[HubspotFieldNames.JobTitle]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorMessage={jobTitleError}
        />
      </fieldset>

      <SelectField
        name={HubspotFieldNames.Industry}
        label={HubspotFieldLabels.Industry}
        options={Object.entries(HubspotIndustryDropdownOptions).map(([label, value]) => ({ label, value }))}
        placeholder="Select one"
        value={formik.values[HubspotFieldNames.Industry]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        errorMessage={industryError}
      />

      <fieldset className="grid grid-cols-2 gap-s">
        <PhoneField
          name={HubspotFieldNames.Phone}
          label={HubspotFieldLabels.Phone}
          value={formik.values[HubspotFieldNames.Phone]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorMessage={phoneError}
        />
        <SelectField
          name={HubspotFieldNames.CompanySize}
          label={HubspotFieldLabels.CompanySize}
          options={Object.entries(HubspotCompanySizeDropdownOptions).map(([label, value]) => ({ label, value }))}
          placeholder="Select one"
          value={formik.values[HubspotFieldNames.CompanySize]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorMessage={companySizeError}
        />
      </fieldset>

      {gdprOptIn && (
        <Checkbox
          checked={gdprOptIn.checked}
          onChange={(e) => gdprOptIn.onToggle(e.target.checked)}
          label={<Text type="caption2">I <span className="font-500">do</span> want to receive marketing communications from Front.</Text>}
          wrapperClassName="mt-3xs"
        />
      )}
      <ButtonElement
        variant="primary"
        buttonType="filled"
        className="delay-100"
        text={formik.isSubmitting ? 'Submitting...' : emailState?.checking ? 'Validating email...' : 'Submit'}
        disabled={isSubmitDisabled}
      />
      <DisclaimerText type="trial" />
    </form>
  );
};

export default TrialPersonalInfoForm;
